import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';

const MOBILE_MAX_WIDTH = 1024;
const BANNER_DISMISSED_KEY = 'rnk_mobile_banner_dismissed';

/** Routes where softer, conversion-friendly copy is used. */
const CONVERSION_PATHS = ['/login', '/register', '/onboarding'];

/**
 * Routes that are deliberately mobile-first surfaces. We don't warn the user
 * that they should be on desktop here — doing so undermines confidence in
 * features that were built specifically for phone use.
 */
const MOBILE_FIRST_PATHS = ['/fixes'];

const isMobileViewport = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < MOBILE_MAX_WIDTH;
};

/**
 * Soft mobile banner — shows a dismissible notice on small screens.
 * Must be rendered inside BrowserRouter for useLocation access.
 *
 * Route-aware:
 * - On login/register/onboarding: softer copy for conversion safety
 * - On in-product pages: standard desktop-first recommendation
 */
const DesktopOnlyGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState<boolean>(isMobileViewport());
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return sessionStorage.getItem(BANNER_DISMISSED_KEY) === 'true'; } catch { return false; }
  });

  const onConversionRoute = CONVERSION_PATHS.some(p => location.pathname.startsWith(p));
  const onMobileFirstRoute = MOBILE_FIRST_PATHS.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    const update = () => setIsMobile(isMobileViewport());
    const media = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH - 1}px)`);
    update();
    if (media.addEventListener) {
      media.addEventListener('change', update);
    } else {
      media.addListener(update);
    }
    window.addEventListener('resize', update);
    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', update);
      } else {
        media.removeListener(update);
      }
      window.removeEventListener('resize', update);
    };
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(BANNER_DISMISSED_KEY, 'true'); } catch { /* noop */ }
  };

  // No banner at all on conversion routes (don't warn at the point of signup)
  // or on mobile-first surfaces like /fixes (the page is the feature on mobile —
  // a "use desktop instead" warning here actively undermines confidence in it).
  const showBanner = isMobile && !dismissed && !onConversionRoute && !onMobileFirstRoute;

  return (
    <>
      {showBanner && (
        <div className="fixed top-0 left-0 right-0 z-[9999] bg-gray-800 text-gray-200 px-4 py-2 flex items-center justify-between text-xs sm:text-sm border-b border-gray-700">
          <span>Some features work best on a larger screen. Try desktop for the full experience.</span>
          <button onClick={handleDismiss} className="ml-2 p-1 hover:bg-gray-700 rounded" aria-label="Dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <div className={showBanner ? 'pt-10' : ''}>
        {children}
      </div>
    </>
  );
};

export default DesktopOnlyGate;
